/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
    addMonths, addYears, isAfter, isBefore, isSameDay, isSameMonth, isWithinInterval, max, min,
} from 'date-fns';
import { DateRange, DefinedRange, NavigationAction } from '../types';
import { getValidatedMonths, parseOptionalDate } from '../utils';
import { getDefaultRanges } from '../defaults';
import Menu from './Menu';
import { Marker, MARKERS } from './Markers';
import { Menu as MUIMenu } from '@mui/material';
import Fade from '@mui/material/Fade';

interface DateRangePickerProps {
    open?: boolean;
    anchorEl?: HTMLElement;
    initialDateRange?: DateRange;
    definedRanges?: DefinedRange[];
    minDate?: Date | string;
    maxDate?: Date | string;
    onChange?: (dateRange: DateRange) => void;
    onClose?: () => void;
    setStartDate?: (startDate: Date) => void;
    setEndDate?: (endDate: Date) => void;
    locale?: Locale;
}

const DateRangePicker: React.FunctionComponent<DateRangePickerProps> = (
    props: DateRangePickerProps,
) => {
    const today = new Date();

    const {
        open,
        anchorEl,
        onChange,
        onClose,
        initialDateRange,
        minDate,
        maxDate,
        definedRanges = getDefaultRanges(new Date(), props.locale),
        locale,
        setStartDate,
        setEndDate
    } = props;

    const minDateValid = parseOptionalDate(minDate, addYears(today, -10));
    const maxDateValid = parseOptionalDate(maxDate, addYears(today, 10));
    const [intialFirstMonth, initialSecondMonth] = getValidatedMonths(
        initialDateRange || {},
        minDateValid,
        maxDateValid,
    );

    const [dateRange, setDateRange] = React.useState<DateRange>({ ...initialDateRange });
    const [hoverDay, setHoverDay] = React.useState<Date>();
    const [firstMonth, setFirstMonth] = React.useState<Date>(intialFirstMonth || today);
    const [secondMonth, setSecondMonth] = React.useState<Date>(
        initialSecondMonth || addMonths(firstMonth, 1),
    );

    const { startDate, endDate } = dateRange;

    // handlers
    const setFirstMonthValidated = (date: Date) => {
        // if (isBefore(date, secondMonth)) {
        //     setFirstMonth(date);
        // }
        setFirstMonth(date);
    };

    const setSecondMonthValidated = (date: Date) => {
        // if (isAfter(date, firstMonth)) {
        //     setSecondMonth(date);
        // }
        setSecondMonth(date);
    };

    const setDateRangeValidated = (range: DateRange) => {
        let { startDate: newStart, endDate: newEnd } = range;

        if (newStart && newEnd) {
            range.startDate = newStart = max([newStart, minDateValid]);
            range.endDate = newEnd = min([newEnd, maxDateValid]);

            setDateRange(range);
            onChange(range);

            setFirstMonth(newStart);
            setSecondMonth(isSameMonth(newStart, newEnd) ? addMonths(newStart, 1) : newEnd);
        } else {
            const emptyRange = {};

            setDateRange(emptyRange);
            onChange(emptyRange);

            setFirstMonth(today);
            setSecondMonth(addMonths(firstMonth, 1));
        }
    };

    const onDayClick = (day: Date) => {
        if (startDate && !endDate && !isBefore(day, startDate)) {
            const newRange = { startDate, endDate: day };
            onChange(newRange);
            setDateRange(newRange);
        } else {
            setDateRange({ startDate: day, endDate: undefined });
        }
        setHoverDay(day);
    };

    const onMonthNavigate = (marker: Marker, action: NavigationAction) => {
        if (marker === MARKERS.FIRST_MONTH) {
            const firstNew = addMonths(firstMonth, action);
            if (isBefore(firstNew, secondMonth)) setFirstMonth(firstNew);
        } else {
            const secondNew = addMonths(secondMonth, action);
            if (isBefore(firstMonth, secondNew)) setSecondMonth(secondNew);
        }
    };

    const onDayHover = (date: Date) => {
        if (startDate && !endDate) {
            if (!hoverDay || !isSameDay(date, hoverDay)) {
                setHoverDay(date);
            }
        }
    };

    // helpers
    const inHoverRange = (day: Date) => (startDate
        && !endDate
        && hoverDay
        && isAfter(hoverDay, startDate)
        && isWithinInterval(day, { start: startDate, end: hoverDay })) as boolean;

    const helpers = {
        inHoverRange,
    };

    const handlers = {
        onDayClick,
        onDayHover,
        onMonthNavigate,
    };

    React.useEffect(() => {
        const { startDate, endDate } = initialDateRange;
        setDateRange(initialDateRange);
        if (startDate && endDate) {
            setFirstMonth(startDate);
            setSecondMonth(isSameMonth(startDate, endDate) ? addMonths(startDate, 1) : endDate);
        }
    }, [initialDateRange]);

    React.useEffect(() => {
        const { startDate, endDate } = dateRange;
        setStartDate(startDate);
        setEndDate(endDate);
        if (startDate && endDate) {
            onClose();
        }
    }, [dateRange]);

    // const [firstDateValue, setFirstDateValue] = React.useState('');
    // const handleFirstDateChange = (e: { target: { value: any; }; }) => {
    //     const { value } = e.target;
    //     clearTimeout(firstDateChange);
    //     firstDateChange = setTimeout(() => {
    //         if (!isNaN(Date.parse(value))) {
    //             const startDate = new Date(value);
    //             const endDate = dateRange.endDate ?? today;
    //             if (endDate.getTime() > startDate.getTime()) {
    //                 setDateRange({ startDate, endDate });
    //             }
    //             else {
    //                 setDateRange({ startDate: null, endDate });
    //             }
    //         }
    //     }, 1500);
    // };

    const [openMenu, setOpenMenu] = React.useState(open);

    return (
        <>
            <MUIMenu
                id="fade-menu"
                MenuListProps={{
                    'aria-labelledby': 'fade-button',
                    'disablePadding': true
                }}
                anchorEl={anchorEl}
                open={open}
                onClose={onClose}
                TransitionComponent={Fade}
                disableAutoFocus={false}
                disableEnforceFocus={false}
            >
                <Menu
                    dateRange={dateRange}
                    minDate={minDateValid}
                    maxDate={maxDateValid}
                    ranges={definedRanges}
                    firstMonth={firstMonth}
                    secondMonth={secondMonth}
                    setFirstMonth={setFirstMonthValidated}
                    setSecondMonth={setSecondMonthValidated}
                    setDateRange={setDateRangeValidated}
                    helpers={helpers}
                    handlers={handlers}
                    locale={locale}
                />
            </MUIMenu>
        </>
    );
};

export default DateRangePicker;
